import { Autocomplete } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { AxiosResponse } from 'axios';
import PmiTextField from 'shell/components/shared/text-field';
import debounce from 'shell/utils/debounce';
import { noop } from 'shell/utils/noop';
import { ItemVM } from 'shell/view-models/item';
import { __PERSON_API_URL__ } from 'shell/constants/environment';
import { Operation } from 'shell/view-models/operation';

interface ProfessionDescriptionAutoListProps {
  disabled?: boolean;
  value?: ItemVM | null;
  onChange: (item: ItemVM | null) => void;
  error?: boolean;
  required?: boolean;
  gender?: string;
  label?: string;
  id?: string;
}

interface ProfessionDescription {
  descrizioneBreveMaschile: string;
  posizioneProfessionale: string;
  descrizioneMaschile: string;
  key: {
    chiave: string;
  };
  descrizioneFemminile: string;
  descrizioneBreveFemminile: string;
}

const ProfessionDescriptionAutoListComponent = ({
  disabled,
  onChange,
  required,
  value: selectedValue,
  error,
  gender = 'M',
  label = 'Professione',
  id = 'profession-auto-list',
}: ProfessionDescriptionAutoListProps): React.ReactElement => {
  const [professionDescription, getProfessionDescription] = useHttpFetch<
    HttpResponseList<ProfessionDescription>,
    Array<ItemVM>
  >(`${__PERSON_API_URL__}/persona/professioni`, {
    onBootstrap: false,
    initialData: [],
    options: {
      loading: false,
      errorCatching: true,
    },
    transformer: (response: AxiosResponse<HttpResponseList<ProfessionDescription>>) => {
      const { data } = response;

      return data?.payload.list.map((item) => ({
        id: item.key.chiave,
        value: gender === 'M' ? item.descrizioneMaschile : item.descrizioneFemminile,
      })) as Array<ItemVM>;
    },
  });

  const defaultProps = useMemo(
    () => ({
      options: professionDescription.data as Array<ItemVM>,
      getOptionLabel: (option: ItemVM) => {
        return option.value;
      },
    }),
    [professionDescription]
  );

  const handleSearch = debounce<string>(
    useCallback(
      (value) => {
        const { abort = noop } = professionDescription;

        abort();

        getProfessionDescription({
          params: {},
          paramsSerializer: () =>
            `filters=descrizione:${value}:${Operation.LIKE}&filters=sesso:${gender}:${Operation.EQUALS}`,
        });
      },
      [professionDescription.abort, getProfessionDescription]
    )
  );

  return (
    <Autocomplete
      {...defaultProps}
      id={id}
      noOptionsText="Nessun elemento"
      loadingText="Caricamento..."
      disabled={disabled}
      value={selectedValue}
      onChange={(event, element) => {
        event.stopPropagation();
        onChange(element);
      }}
      onInputChange={(event, value, reason) => {
        if (value.length >= 3 && reason === 'input') {
          handleSearch(value);
        }
      }}
      clearOnBlur={false}
      loading={professionDescription.isLoading}
      style={{ flex: 1 }}
      renderInput={(params) => (
        <PmiTextField
          {...params}
          label={required ? `${label} *` : label}
          variant="outlined"
          error={error}
        />
      )}
    />
  );
};

ProfessionDescriptionAutoListComponent.displayName =
  'Profession description autocomplete list component';

const ProfessionDescriptionAutoList = memo(ProfessionDescriptionAutoListComponent);

export default ProfessionDescriptionAutoList as typeof ProfessionDescriptionAutoListComponent;
